import { useCallback, useMemo, useState } from 'react';

import type { SeguradoBeneficiarioFormValues } from '../../types/vida/cadastralMaintenance/beneficiarioFormTypes';
import type { NewInsuredFormValues } from '../../types/vida/cadastralMaintenance/newInsuredFormTypes';
import type { PersonFormSection } from '../../types/vida/cadastralMaintenance/personFormTypes';
import type { SeguradoDetailBeneficiaryItem } from '../../types/vida/maintenanceSegurado/maintenanceSeguradoType';
import type { BeneficiarioTableItem } from '../../types/vida/table/beneficiariosTableType';
import {
  createEmptySeguradoBeneficiarioFormValues,
  mapSeguradoBeneficiarioFormToDetailItem,
  mapSeguradoDetailBeneficiaryToFormValues,
} from '../../utils/beneficiarioFormUtils';
import { mapBeneficiariosToTableRows } from '../../utils/selfBillingUtil';
import { usePersonFormWizard } from './usePersonFormWizard';

export type BeneficiariosScreen = 'list' | 'detail' | null;
export type ConjugueScreen = 'panel' | 'form' | null;
export type SeguradoWizardStep = PersonFormSection | 'beneficiarios' | 'conjugue';

type UseSeguradoRelatedPanelsParams = {
  formKey: string | number;
  beneficiarios: SeguradoDetailBeneficiaryItem[];
  setBeneficiarios: (items: SeguradoDetailBeneficiaryItem[]) => void;
  formSection: PersonFormSection;
  /** When true, drafts are kept while navigating between panels. */
  preserveDrafts?: boolean;
  numVidaPreviaFat?: number;
  persistedConjugueValues?: NewInsuredFormValues | null;
  showConjugueStep?: boolean;
};

export const useSeguradoRelatedPanels = ({
  formKey,
  beneficiarios,
  setBeneficiarios,
  formSection,
  preserveDrafts = true,
  numVidaPreviaFat,
  persistedConjugueValues = null,
  showConjugueStep = true,
}: UseSeguradoRelatedPanelsParams) => {
  const [beneficiariosScreen, setBeneficiariosScreen] = useState<BeneficiariosScreen>(null);
  const [selectedBeneficiario, setSelectedBeneficiario] = useState<BeneficiarioTableItem | null>(null);
  const [beneficiarioDraft, setBeneficiarioDraft] = useState<SeguradoBeneficiarioFormValues | null>(null);
  const [beneficiarioFormInitialValues, setBeneficiarioFormInitialValues] = useState<SeguradoBeneficiarioFormValues | null>(null);
  const [isBeneficiarioValid, setIsBeneficiarioValid] = useState(false);
  const [conjugueScreen, setConjugueScreen] = useState<ConjugueScreen>(null);
  const [conjugueDraftValues, setConjugueDraftValues] = useState<NewInsuredFormValues | null>(null);
  const [conjugueFormInitialValues, setConjugueFormInitialValues] = useState<NewInsuredFormValues | null>(null);
  const [isConjugueFormValid, setIsConjugueFormValid] = useState(false);

  const {
    sectionControlRef: conjugueSectionControlRef,
    onActiveSectionChange: onConjugueActiveSectionChange,
    isLastSection: isConjugueLastSection,
    handleContinueOrSave: handleConjugueContinueOrSave,
  } = usePersonFormWizard(`conjugue-${formKey}-${conjugueScreen ?? 'none'}`);

  const beneficiariosRows = useMemo(
    () => mapBeneficiariosToTableRows(beneficiarios),
    [beneficiarios],
  );

  const isBeneficiariosListActive = beneficiariosScreen === 'list';
  const isBeneficiariosDetailActive = beneficiariosScreen === 'detail';
  const isConjuguePanelActive = conjugueScreen === 'panel';
  const isConjugueFormActive = conjugueScreen === 'form';
  const isConjugueScreenActive = isConjuguePanelActive || isConjugueFormActive;

  const wizardSteps = useMemo((): SeguradoWizardStep[] => {
    const steps: SeguradoWizardStep[] = ['personData', 'personAddress', 'insuranceInfo', 'beneficiarios'];
    if (showConjugueStep) {
      steps.push('conjugue');
    }
    return steps;
  }, [showConjugueStep]);

  const currentStep: SeguradoWizardStep = isConjugueScreenActive
    ? 'conjugue'
    : (isBeneficiariosListActive || isBeneficiariosDetailActive)
      ? 'beneficiarios'
      : formSection;

  const isWizardLastSection = !isBeneficiariosDetailActive
    && !isConjugueFormActive
    && currentStep === wizardSteps[wizardSteps.length - 1];

  const conjugueDisplayValues = isConjugueFormActive
    ? conjugueFormInitialValues
    : (conjugueDraftValues ?? persistedConjugueValues);

  const resetBeneficiariosScreen = useCallback((options?: { clearDraft?: boolean }) => {
    setBeneficiariosScreen(null);
    if (options?.clearDraft !== false) {
      setSelectedBeneficiario(null);
      setBeneficiarioDraft(null);
      setBeneficiarioFormInitialValues(null);
      setIsBeneficiarioValid(false);
    }
  }, []);

  const resetConjugueScreen = useCallback((options?: { clearDraft?: boolean }) => {
    setConjugueScreen(null);
    if (options?.clearDraft !== false) {
      setConjugueDraftValues(null);
      setConjugueFormInitialValues(null);
      setIsConjugueFormValid(false);
    }
  }, []);

  const clearDraftOnNavigate = !preserveDrafts;

  const handleOpenBeneficiarios = useCallback(() => {
    resetConjugueScreen({ clearDraft: clearDraftOnNavigate });
    setBeneficiariosScreen('list');
    if (clearDraftOnNavigate) {
      setSelectedBeneficiario(null);
      setBeneficiarioDraft(null);
      setBeneficiarioFormInitialValues(null);
      setIsBeneficiarioValid(false);
    }
  }, [clearDraftOnNavigate, resetConjugueScreen]);

  const handleOpenConjugue = useCallback(() => {
    resetBeneficiariosScreen({ clearDraft: clearDraftOnNavigate });
    if (clearDraftOnNavigate) {
      setConjugueDraftValues(null);
      setConjugueFormInitialValues(null);
      setIsConjugueFormValid(false);
    }
    setConjugueScreen('panel');
  }, [clearDraftOnNavigate, resetBeneficiariosScreen]);

  const handleExitExtraPanels = useCallback(() => {
    resetBeneficiariosScreen({ clearDraft: clearDraftOnNavigate });
    resetConjugueScreen({ clearDraft: clearDraftOnNavigate });
  }, [clearDraftOnNavigate, resetBeneficiariosScreen, resetConjugueScreen]);

  const handleAddConjugue = useCallback(() => {
    setConjugueFormInitialValues(conjugueDraftValues);
    setConjugueScreen('form');
  }, [conjugueDraftValues]);

  const handleEditConjugue = useCallback(() => {
    const currentValues = conjugueDraftValues ?? persistedConjugueValues;
    setConjugueFormInitialValues(currentValues);
    setConjugueScreen('form');
  }, [conjugueDraftValues, persistedConjugueValues]);

  const handleAddBeneficiario = useCallback(() => {
    if (beneficiarioDraft && !selectedBeneficiario) {
      setBeneficiarioFormInitialValues(beneficiarioDraft);
      setBeneficiariosScreen('detail');
      return;
    }

    setSelectedBeneficiario(null);
    const emptyDraft = createEmptySeguradoBeneficiarioFormValues(
      `novo-${Date.now()}`,
      numVidaPreviaFat,
    );
    setBeneficiarioDraft(emptyDraft);
    setBeneficiarioFormInitialValues(emptyDraft);
    setBeneficiariosScreen('detail');
  }, [beneficiarioDraft, numVidaPreviaFat, selectedBeneficiario]);

  const handleEditBeneficiario = useCallback((row: BeneficiarioTableItem) => {
    const keepDraft = selectedBeneficiario?.id === row.id && beneficiarioDraft
      ? beneficiarioDraft
      : mapSeguradoDetailBeneficiaryToFormValues(row.source, 0);
    setSelectedBeneficiario(row);
    setBeneficiarioDraft(keepDraft);
    setBeneficiarioFormInitialValues(keepDraft);
    setBeneficiariosScreen('detail');
  }, [beneficiarioDraft, selectedBeneficiario]);

  const handleSaveBeneficiario = useCallback(() => {
    if (!beneficiarioDraft || !isBeneficiarioValid) {
      return;
    }

    const nextItem = mapSeguradoBeneficiarioFormToDetailItem(beneficiarioDraft);
    const selectedIndex = selectedBeneficiario
      ? beneficiariosRows.findIndex((row) => row.id === selectedBeneficiario.id)
      : -1;

    if (selectedIndex >= 0) {
      setBeneficiarios(
        beneficiarios.map((item, index) => (index === selectedIndex ? nextItem : item)),
      );
    } else {
      setBeneficiarios([...beneficiarios, nextItem]);
    }

    setBeneficiariosScreen('list');
    setSelectedBeneficiario(null);
    setBeneficiarioDraft(null);
    setBeneficiarioFormInitialValues(null);
  }, [
    beneficiarioDraft,
    beneficiarios,
    beneficiariosRows,
    isBeneficiarioValid,
    selectedBeneficiario,
    setBeneficiarios,
  ]);

  const confirmConjugueAndReturnToPanel = useCallback(() => {
    setConjugueScreen('panel');
    setConjugueFormInitialValues(null);
  }, []);

  const handleRelatedPanelsContinue = useCallback((options: {
    onSave: () => void | Promise<void>;
    goToNextFormSection: () => void;
  }): boolean => {
    if (isConjugueFormActive) {
      handleConjugueContinueOrSave(confirmConjugueAndReturnToPanel);
      return true;
    }

    if (isWizardLastSection) {
      void options.onSave();
      return true;
    }

    if (currentStep === 'beneficiarios') {
      if (isBeneficiariosDetailActive) {
        if (beneficiarioDraft && isBeneficiarioValid) {
          handleSaveBeneficiario();
        } else {
          setBeneficiariosScreen('list');
          setBeneficiarioFormInitialValues(null);
        }
        return true;
      }
      if (showConjugueStep) {
        handleOpenConjugue();
        return true;
      }
      void options.onSave();
      return true;
    }

    if (currentStep === 'insuranceInfo') {
      handleOpenBeneficiarios();
      return true;
    }

    options.goToNextFormSection();
    return true;
  }, [
    beneficiarioDraft,
    confirmConjugueAndReturnToPanel,
    currentStep,
    handleConjugueContinueOrSave,
    handleOpenBeneficiarios,
    handleOpenConjugue,
    handleSaveBeneficiario,
    isBeneficiarioValid,
    isBeneficiariosDetailActive,
    isConjugueFormActive,
    isWizardLastSection,
    showConjugueStep,
  ]);

  const handleRelatedPanelsBack = useCallback((options?: {
    onExit?: () => void;
    clearDraftOnBack?: boolean;
  }): boolean => {
    const clearDraft = options?.clearDraftOnBack ?? clearDraftOnNavigate;

    if (isConjugueFormActive) {
      setConjugueScreen('panel');
      setConjugueFormInitialValues(null);
      if (clearDraft) {
        setConjugueDraftValues(null);
        setIsConjugueFormValid(false);
      }
      return true;
    }
    if (isConjuguePanelActive) {
      resetConjugueScreen({ clearDraft });
      return true;
    }
    if (isBeneficiariosDetailActive) {
      setBeneficiariosScreen('list');
      setBeneficiarioFormInitialValues(null);
      if (clearDraft) {
        setSelectedBeneficiario(null);
        setBeneficiarioDraft(null);
        setIsBeneficiarioValid(false);
      }
      return true;
    }
    if (isBeneficiariosListActive) {
      resetBeneficiariosScreen({ clearDraft });
      return true;
    }

    options?.onExit?.();
    return false;
  }, [
    clearDraftOnNavigate,
    isBeneficiariosDetailActive,
    isBeneficiariosListActive,
    isConjugueFormActive,
    isConjuguePanelActive,
    resetBeneficiariosScreen,
    resetConjugueScreen,
  ]);

  const resetAllRelatedPanels = useCallback(() => {
    resetBeneficiariosScreen();
    resetConjugueScreen();
  }, [resetBeneficiariosScreen, resetConjugueScreen]);

  return {
    beneficiariosRows,
    beneficiariosScreen,
    selectedBeneficiario,
    beneficiarioDraft,
    beneficiarioFormInitialValues,
    isBeneficiarioValid,
    setBeneficiarioDraft,
    setIsBeneficiarioValid,
    conjugueDraftValues,
    conjugueFormInitialValues,
    conjugueDisplayValues,
    isConjugueFormValid,
    setConjugueDraftValues,
    setIsConjugueFormValid,
    conjugueSectionControlRef,
    onConjugueActiveSectionChange,
    isConjugueLastSection,
    isBeneficiariosListActive,
    isBeneficiariosDetailActive,
    isConjuguePanelActive,
    isConjugueFormActive,
    isConjugueScreenActive,
    currentStep,
    isWizardLastSection,
    resetBeneficiariosScreen,
    resetConjugueScreen,
    resetAllRelatedPanels,
    handleOpenBeneficiarios,
    handleOpenConjugue,
    handleExitExtraPanels,
    handleAddConjugue,
    handleEditConjugue,
    handleAddBeneficiario,
    handleEditBeneficiario,
    handleSaveBeneficiario,
    handleRelatedPanelsContinue,
    handleRelatedPanelsBack,
    setBeneficiariosScreen,
    setConjugueScreen,
  };
};
